import React from 'react';
import { useMessageContext, useChannelStateContext } from 'stream-chat-react';
import type { StreamChatType } from '../types';
import { SessionManager } from '../../services/SessionManager/SessionManager';
import './CustomMessageStatus.scss';

/**
 * CustomMessageStatus Component
 * 
 * Displays WhatsApp-style message status indicators for chat messages:
 * - Clock icon (animated): Message is sending
 * - Single checkmark (gray): Message delivered but not read
 * - Double checkmark (blue): Message read by recipient
 * 
 * Also displays the message timestamp next to the status icons.
 * Only shown for messages sent by the current user.
 */
export const CustomMessageStatus: React.FC = () => {
  const { message } = useMessageContext<StreamChatType>();
  const { channel } = useChannelStateContext<StreamChatType>();

  // Only show status for messages sent by the current user
  const isMyMessage = message.user?.id === SessionManager.shared.buyer?.id;
  
  if (!isMyMessage || message.type === 'error' || message.status === 'failed') {
    return null;
  }

  // Check if message is read by checking read receipts
  const readBy = Object.values(channel.state.read || {}).filter(
    (readState) => 
      readState.user?.id !== message.user?.id && // Not the sender
      readState.last_read && 
      message.created_at &&
      new Date(readState.last_read) >= new Date(message.created_at)
  );

  const isRead = readBy.length > 0;
  const isDelivered = message.status === 'received' || isRead;
  const isSending = message.status === 'sending';

  // Format timestamp (e.g., "11:27 PM")
  const getFormattedTime = () => {
    if (!message.created_at) return '';
    
    const date = new Date(message.created_at);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="custom-message-status">
      <span className="message-timestamp">{getFormattedTime()}</span>
      
      {isSending && (
        <svg 
          className="status-icon status-sending" 
          width="16" 
          height="16" 
          viewBox="0 0 16 16"
        >
          <circle 
            cx="8" 
            cy="8" 
            r="6" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1.5"
            opacity="0.4"
          />
        </svg>
      )}
      
      {!isSending && !isRead && isDelivered && (
        <svg 
          className="status-icon status-delivered" 
          width="16" 
          height="16" 
          viewBox="0 0 16 16"
        >
          {/* Single checkmark - Delivered but not read */}
          <path 
            d="M4 8.5L6.5 11L12 5.5" 
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      
      {isRead && (
        <svg 
          className="status-icon status-read" 
          width="18" 
          height="16" 
          viewBox="0 0 18 16"
        >
          {/* Double checkmark - Read (WhatsApp style) */}
          <path 
            d="M2 8.5L4.5 11L10 5.5" 
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path 
            d="M6 8.5L8.5 11L14 5.5" 
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

